import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/labels")({
  component: RouteComponent,
});

function RouteComponent() {
  // LabelViewSet's read_permission ("inventory.stock.view") gates
  // list/retrieve too, with no degrade-to-own-rows fallback, so the whole
  // page is protected up front; write_permission ("inventory.item.manage")
  // gates just the write UI within.
  return (
    <ProtectedRoute requiredPermission="inventory.stock.view">
      <LabelsPage />
    </ProtectedRoute>
  );
}

import { useState } from "react";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import {
  useFetchLabels,
  useLabelMutations,
} from "@/hooks/api/inventory/useCollectionsLabels";
import { LabelTable } from "@/components/inventory/tables/LabelTable";
import { LabelFormSheet } from "@/components/inventory/forms/LabelFormSheet";
import type { InventoryLabel, InventoryLabelPayload } from "@/types/inventory";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function LabelsPage() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "inventory.item.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const labelsQuery = useFetchLabels({ page_size: pageSize, page });
  const labels = labelsQuery.data?.results ?? [];
  const count = labelsQuery.data?.count ?? 0;

  const { createLabel, updateLabel, deleteLabel } = useLabelMutations();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<InventoryLabel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InventoryLabel | null>(null);

  const isMutating =
    createLabel.isPending || updateLabel.isPending || deleteLabel.isPending;
  const isLoading = labelsQuery.isPending || isMutating;
  const isFetching = labelsQuery.isFetching || isMutating;

  const handleSubmit = async (data: InventoryLabelPayload) => {
    if (editing) {
      await updateLabel.mutateAsync({ id: editing.id, payload: data });
      toast.success("Label updated");
    } else {
      await createLabel.mutateAsync(data);
      toast.success("Label created");
    }
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteLabel.mutateAsync({ id: deleteTarget.id });
      toast.success("Label deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Item Labels"
          description="Tag items for quick filtering and identification"
          onAdd={
            canManage
              ? () => {
                  setEditing(null);
                  setIsViewOnly(false);
                  setIsSheetOpen(true);
                }
              : undefined
          }
          addLabel="Add Label"
          isLoading={isFetching}
        />

        <LabelTable
          labels={labels}
          isLoading={isLoading}
          onView={(l) => {
            setEditing(l);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(l) => {
            setEditing(l);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(l) => setDeleteTarget(l)}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <LabelFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleSubmit}
          editing={editing}
          isLoading={isMutating}
          isViewOnly={isViewOnly}
        />

        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Label"
          description={`Delete label "${deleteTarget?.name}"?`}
          itemName={deleteTarget?.name}
          isLoading={deleteLabel.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
